// 쿠폰 공통 타입 + 할인 계산.
// 사용자 앱(쿠폰함/체크아웃), 관리자(주문 표시), API 가 공유한다.

import { z } from "zod";

// 쿠폰 유형.
export const COUPON_TYPE = {
  PERCENT: "percent", // 정률 할인 (value = 1~100 %)
  FIXED: "fixed", // 정액 할인 (value = 원)
  FREE_DELIVERY: "free_delivery", // 배달비 무료
} as const;
export type CouponType = (typeof COUPON_TYPE)[keyof typeof COUPON_TYPE];

// 쿠폰 "묶음 종류" - 한 주문에 할인(discount) 1장 + 배달(delivery) 1장 규칙의 분류.
export type CouponKind = "discount" | "delivery";
// 쿠폰 유형을 묶음 종류(할인/배달)로 분류. 한 주문 할인1+배달1 규칙의 기준.
export function couponKind(type: CouponType): CouponKind {
  return type === COUPON_TYPE.FREE_DELIVERY ? "delivery" : "discount";
}

export const COUPON_STATUS = {
  ACTIVE: "active",
  RESERVED: "reserved",
  USED: "used",
} as const;
export type CouponStatus = (typeof COUPON_STATUS)[keyof typeof COUPON_STATUS];

export interface CouponDto {
  id: string;
  name: string;
  type: CouponType;
  value: number;
  status: CouponStatus;
  source: "signup" | "tier" | "admin";
  issuedAt: string;
  expiresAt: string;
  // 최소 주문금액(원). null/0 이면 제한 없음. 상품 합계(subtotal)가 이 값 이상이어야 사용 가능.
  minOrderKrw: number | null;
  // 최대 할인금액(원). 정률(percent) 쿠폰에만 의미가 있다. null/0 이면 상한 없음.
  maxDiscountKrw: number | null;
  // 사용 조건: 주문 방식 제한. null 이면 전체(포장/배달 모두). 'pickup'=포장 전용, 'delivery'=배달 전용.
  applicableOrderType: "pickup" | "delivery" | null;
  // 사용 가능 여부 (상태 active + 미만료). 서버가 계산해 내려준다.
  usable: boolean;
}

// 사용 조건(주문 방식) 표시 라벨.
export const COUPON_ORDER_TYPE_LABEL_KO: Record<"pickup" | "delivery", string> = {
  pickup: "포장 전용",
  delivery: "배달 전용",
};

// 쿠폰의 주문 방식 사용조건 충족 여부. null/undefined(전체)면 항상 true.
export function isCouponOrderTypeAllowed(
  applicableOrderType: "pickup" | "delivery" | null | undefined,
  orderType: "pickup" | "delivery",
): boolean {
  return !applicableOrderType || applicableOrderType === orderType;
}

// 최소 주문금액 조건 충족 여부. minOrderKrw 가 null/0/undefined 면 항상 충족(제한 없음).
export function isCouponMinOrderMet(
  minOrderKrw: number | null | undefined,
  subtotalKrw: number,
): boolean {
  return !minOrderKrw || subtotalKrw >= minOrderKrw;
}

// 할인 쿠폰(percent/fixed)이 subtotal 에 적용했을 때의 할인액. 배달비무료는 0(배달비에서 처리).
//  - percent: 내림 + 최대 할인금액(maxDiscountKrw) 상한 적용(있을 때). fixed: 정액. 둘 다 subtotal 을 넘지 않는다.
//  - 최소 주문금액 조건은 별도(isCouponMinOrderMet)로 검사한다. 미충족이면 호출 측에서 적용 자체를 막는다.
export function computeCouponDiscount(
  type: CouponType,
  value: number,
  subtotalKrw: number,
  maxDiscountKrw?: number | null,
): number {
  if (type === COUPON_TYPE.PERCENT) {
    let discount = Math.floor((subtotalKrw * value) / 100);
    // 정률 할인만 최대 할인금액 상한을 적용한다(정액/배달비무료는 무관).
    if (maxDiscountKrw && maxDiscountKrw > 0) {
      discount = Math.min(discount, maxDiscountKrw);
    }
    return Math.min(subtotalKrw, discount);
  }
  if (type === COUPON_TYPE.FIXED) {
    return Math.min(subtotalKrw, value);
  }
  return 0;
}

// 쿠폰 사용 조건(최소 주문금액/최대 할인)을 사람이 읽는 짧은 문구 배열로 만든다(쿠폰함/체크아웃 표시용).
//  - 최대 할인은 정률 쿠폰일 때만 노출한다.
export function formatCouponConditions(coupon: {
  type: CouponType;
  minOrderKrw: number | null;
  maxDiscountKrw: number | null;
  applicableOrderType?: "pickup" | "delivery" | null;
}): string[] {
  const lines: string[] = [];
  if (coupon.applicableOrderType) {
    lines.push(COUPON_ORDER_TYPE_LABEL_KO[coupon.applicableOrderType]);
  }
  if (coupon.minOrderKrw && coupon.minOrderKrw > 0) {
    lines.push(`${coupon.minOrderKrw.toLocaleString()}원 이상 주문 시`);
  }
  if (coupon.type === COUPON_TYPE.PERCENT && coupon.maxDiscountKrw && coupon.maxDiscountKrw > 0) {
    lines.push(`최대 ${coupon.maxDiscountKrw.toLocaleString()}원 할인`);
  }
  return lines;
}

// 쿠폰 발급 스펙 (관리자 설정: 가입 쿠폰 팩 / 등급 쿠폰 팩 공용).
//  - count 는 발급 장수. validDays 는 발급 시점부터의 유효 일수.
export const couponSpecSchema = z.object({
  type: z.union([
    z.literal(COUPON_TYPE.PERCENT),
    z.literal(COUPON_TYPE.FIXED),
    z.literal(COUPON_TYPE.FREE_DELIVERY),
  ]),
  value: z.number().int().min(0).max(1_000_000),
  name: z.string().trim().min(1).max(30),
  validDays: z.number().int().min(1).max(365),
  count: z.number().int().min(1).max(20),
  // 최소 주문금액(원). 비워두면(null) 제한 없음.
  minOrderKrw: z.number().int().min(0).max(100_000_000).nullable().optional(),
  // 최대 할인금액(원). 정률 할인에만 적용. 비워두면(null) 한도 없음.
  maxDiscountKrw: z.number().int().min(0).max(100_000_000).nullable().optional(),
  // 사용 조건: 주문 방식 제한. 미입력(null)이면 전체 사용 가능. 'pickup'=포장 전용, 'delivery'=배달 전용.
  applicableOrderType: z
    .union([z.literal("pickup"), z.literal("delivery")])
    .nullable()
    .optional(),
});
export type CouponSpec = z.infer<typeof couponSpecSchema>;

// 쿠폰 혜택 표시 라벨 (예: "5% 할인", "3,000원 할인", "배달비 무료").
export function formatCouponBenefit(type: CouponType, value: number): string {
  if (type === COUPON_TYPE.PERCENT) {
    return `${value}% 할인`;
  }
  if (type === COUPON_TYPE.FIXED) {
    return `${value.toLocaleString()}원 할인`;
  }
  return "배달비 무료";
}
